import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, take, takeUntil } from 'rxjs';
import { CrudService } from '../../../../shared/services/crud.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { UserService } from '../../../../shared/services/user.service';
import { CartingConfigViewModel } from '../carting.config.view.model';
import { ICartingConfig } from '../types';

@Injectable({
	providedIn: 'root',
})
export class CartingConfigService {
	public serverConfiguration: ICartingConfig | null = null;

	private _accountCancel$ = new Subject<void>();

	constructor(
		private crudService: CrudService,
		private notificationService: NotificationService,
		private userService: UserService,
		public viewModel: CartingConfigViewModel,
	) {
		this.handleError = this.handleError.bind(this);
		this.userService.accountChanged$.subscribe(() => {
			this._accountCancel$.next();
			this.resetCache();
		});
	}

	private resetCache(): void {
		this.serverConfiguration = null;
		this.viewModel.clear();
	}

	public loadConfiguration(): void {
		this.viewModel.isLoadingConfiguration = true;
		this.viewModel.clear();
		this.crudService
			.get<{ items: ICartingConfig[] }>('/carting/configs')
			.pipe(take(1), takeUntil(this._accountCancel$))
			.subscribe({
				next: (response) => {
					if (response.items.length > 0) {
						this.serverConfiguration = response.items[0];
						this.setViewModelFromServerConfiguration();
					}
					this.viewModel.isLoadingConfiguration = false;
				},
				error: this.handleError,
			});
	}

	private handleError(error: HttpErrorResponse): void {
		this.viewModel.isUpdatingConfiguration = false;
		this.viewModel.isLoadingConfiguration = false;
		this.setViewModelFromServerConfiguration();

		if (error.status === 0) {
			this.notificationService.notify(['carting.errors.network'], [], 'error');
		} else {
			this.notificationService.notify(['carting.errors.unknown'], [], 'error');
		}
	}

	private setViewModelFromServerConfiguration(): void {
		if (!this.serverConfiguration) return;

		const { groupSorting = [], componentSorting = [], splitDoorAndWindowSashes, groupByColors, maxProductionSequenceNumberJumpsInSameWagon, groupingCriterias } = this.serverConfiguration;

		this.viewModel.maxProductionSequenceNumberJumpsInSameWagon = maxProductionSequenceNumberJumpsInSameWagon ?? null;
		this.viewModel.splitDoorAndWindowSashes = splitDoorAndWindowSashes ?? false;
		this.viewModel.groupByColors = groupByColors ?? false;
		this.viewModel.productionRoute = groupingCriterias.productionRoute ?? false;
		this.viewModel.paintInside = groupingCriterias.paintInside ?? false;
		this.viewModel.profileSet = groupingCriterias.profileSet ?? false;
		this.viewModel.profileSetGroup = groupingCriterias.profileSetGroup ?? false;
		this.viewModel.geometry = groupingCriterias.geometry ?? false;

		this.viewModel.setGroupSortingFromServer(groupSorting);
		this.viewModel.setComponentSortingFromServer(componentSorting);

		this.setEnableDisablePriorityFormFields();
	}

	private setEnableDisablePriorityFormFields(): void {
		for (const { property, control } of this.viewModel.groupSortingFormFieldsLookup) {
			if (this.viewModel[property]) {
				control.enable();
			} else {
				control.reset();
				control.disable();
			}
		}

		for (const { property, control } of this.viewModel.componentSortingFormFieldsLookup) {
			if (this.viewModel[property]) {
				control.enable();
			} else {
				control.reset();
				control.disable();
			}
		}
	}

	public updatePriorityFormValidity(): void {
		/**
		 * Form.markAllAsTouched(); form.updateValueAndValidity();
		 *
		 * Will not trigger immediately, so we need to do this instead
		 */
		const sorting = Object.entries(this.viewModel.groupSortingPriorityForm.controls);
		for (const [_, control] of sorting) {
			control.markAsTouched({ onlySelf: true });
			control.updateValueAndValidity();
		}

		const pooling = Object.entries(this.viewModel.componentSortingPriorityForm.controls);
		for (const [_, control] of pooling) {
			control.markAsTouched({ onlySelf: true });
			control.updateValueAndValidity();
		}
	}

	public updateConfiguration(): void {
		this.setEnableDisablePriorityFormFields();
		this.updateOrderAndPriorityValues();
		this.updatePriorityFormValidity();
	}

	public saveConfiguration(): void {
		if (this.serverConfiguration == null) {
			return;
		}

		// saveConfiguration is only invoked by
		// the save button, but we guard anyways
		if (!this.viewModel.areFormsValid) {
			return;
		}

		this.viewModel.isUpdatingConfiguration = true;
		const config = this.getUpdatedConfiguration();
		this.crudService
			.update<ICartingConfig>(config)
			.pipe(take(1))
			.subscribe({
				next: (updated) => {
					this.viewModel.isUpdatingConfiguration = false;
					if (updated) {
						this.serverConfiguration = updated;
						this.notificationService.notify(['masterdata.carting.config.update.success.heading'], ['masterdata.carting.config.update.success.content'], 'success');
					}
				},
				error: this.handleError,
			});
	}

	private updateOrderAndPriorityValues(): void {
		this.viewModel.nullifyDisabledSortingStrategies();
	}

	private getUpdatedConfiguration(): ICartingConfig {
		return {
			self: this.serverConfiguration?.self ?? '',
			groupingCriterias: {
				geometry: this.viewModel.geometry,
				paintInside: this.viewModel.paintInside,
				productionRoute: this.viewModel.productionRoute,
				profileSet: this.viewModel.profileSet,
				profileSetGroup: this.viewModel.profileSetGroup,
			},
			maxProductionSequenceNumberJumpsInSameWagon: this.toNumber(this.viewModel.maxProductionSequenceNumberJumpsInSameWagon),
			splitDoorAndWindowSashes: this.viewModel?.splitDoorAndWindowSashes ?? false,
			groupByColors: this.viewModel?.groupByColors ?? false,
			groupSorting: this.viewModel.getGroupSortingFromViewModel(),
			componentSorting: this.viewModel.getComponentSortingFromViewModel(),
		};
	}

	private toNumber(value: unknown): number | null {
		const parsed = Number(value);
		return Number.isNaN(parsed) || typeof value === 'boolean' || value === null || value === '' ? null : parsed;
	}
}
